/**
 * renderHelpers.tsx
 *
 * Shared primitive cards and the ToolRenderProps type used by all domain
 * renderer hooks. Import from here — never re-define in individual hooks.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { parseEnvelope, type ToolEnvelope } from "../toolEnvelope";

// ---------------------------------------------------------------------------
// Shared prop shape (CopilotKit v2 wildcard render — props: any at runtime)
// ---------------------------------------------------------------------------

export interface ToolRenderProps {
  name: string;
  toolCallId: string;
  args: unknown;
  status: "inProgress" | "executing" | "complete";
  result: string | undefined;
}

// ---------------------------------------------------------------------------
// Primitive shared cards
// ---------------------------------------------------------------------------

export function LoadingCard({ label }: { label: string }) {
  return (
    <Card className="my-2 border-border/60">
      <CardHeader className="pb-1 pt-3 px-4">
        <span className="text-muted-foreground text-xs animate-pulse">{label}</span>
      </CardHeader>
    </Card>
  );
}

export function ErrorCard({ summary, error }: { summary: string; error?: string }) {
  return (
    <Card className="my-2 border-border/60">
      <CardHeader className="pb-1 pt-3 px-4">
        <CardTitle className="text-sm font-medium text-destructive">{summary}</CardTitle>
      </CardHeader>
      {error && (
        <CardContent className="pt-0 pb-3 px-4">
          <p className="text-xs text-destructive">{error}</p>
        </CardContent>
      )}
    </Card>
  );
}

export function RawCard({ result }: { result: string | undefined }) {
  return (
    <Card className="my-2 border-border/60">
      <CardContent className="pt-3 pb-3 px-4">
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">
          {typeof result === "string" ? result : JSON.stringify(result)}
        </pre>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wildcard fallback card (used only by CopilotToolRenderers)
//
// All 24 OneThing tools have named renderers — the wildcard only catches
// OneClaw internal/system calls (context-injection, bash, ls, identity, etc.).
// Those are not user-facing, so we render nothing for non-envelope results.
// Only a recognizable OneThing envelope gets a card; everything else is silent.
// ---------------------------------------------------------------------------

export function WildcardCard({ result }: ToolRenderProps) {
  const envelope = result != null ? parseEnvelope(result) : null;

  // Unknown/internal tool or un-parseable result → silent (no raw JSON dump)
  if (!envelope) return null;

  // Recognized OneThing envelope: show summary + kind badge
  return (
    <Card className="my-2 border-border/60">
      <CardHeader className="pb-1 pt-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-sm font-medium leading-snug">
            {envelope.summary}
          </CardTitle>
          <Badge
            variant={envelope.ok ? "secondary" : "destructive"}
            className="text-xs font-mono"
          >
            {envelope.kind}
          </Badge>
        </div>
      </CardHeader>
      {!envelope.ok && envelope.error && (
        <CardContent className="pt-0 pb-3 px-4">
          <p className="text-xs text-destructive">{envelope.error}</p>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Utility: parse envelope from props; returns null on failure
// ---------------------------------------------------------------------------

export function resolveEnvelope(props: ToolRenderProps): ToolEnvelope | null {
  return props.result != null ? parseEnvelope(props.result) : null;
}
